// Third-party packages
import React, { useContext, useEffect, useState } from 'react'
import {
  Button,
  Card,
  Container,
  Header,
  Icon,
  Grid,
  Placeholder,
  Segment,
} from 'semantic-ui-react'

// Main packages
import * as api from '../api'
import { AppContext } from './app_context'
import SectionFooter from './section_footer'
import SectionHeader from './section_header'
import SectionTitle from './section_title'

const HomePage = () => {

  // Global app state
  const app = useContext(AppContext)

  var title = 'BitGeeks Coffee – The Java Your Brain Codes On'
  var subtitle = 'From the first sip to the final bug fix, power up with BitGeeks.'

  const [pending, setPending] = useState({})
  const [products, setProducts] = useState([])
  const [recommendations, setRecommendations] = useState([])
  const [recommendationsLoading, setRecommendationsLoading] = useState(true)
  
  useEffect(() => {
    const getProducts = async () => {
      const startTime = Date.now();
      
      try {
        const res = await api.productSearch({
          query: '',
          page: {
            size: 6
          }
        })
        const productsData = res.data.results || [];
        setProducts(productsData);
        
        // Track product listing viewed
        if (window.faroTracker) {
          window.faroTracker.trackEvent('product_listing_viewed', {
            products_count: productsData.length,
            load_time: Date.now() - startTime,
            page: 'homepage',
            total_available: res.data.total_results || 0
          });
          
          // Track product categories visible
          const categories = new Set();
          const types = new Set();
          productsData.forEach(product => {
            categories.add(product.category || 'uncategorized');
            types.add(product.type || 'unknown');
          });
          
          window.faroTracker.trackEvent('homepage_products_analyzed', {
            unique_categories: Array.from(categories),
            unique_types: Array.from(types),
            category_count: categories.size,
            type_count: types.size
          });
        }
      } catch (error) {
        console.error('Product loading failed:', error);
        if (window.faroTracker) {
          window.faroTracker.trackError(error, {
            context: 'homepage_product_load',
            duration: Date.now() - startTime
          });
        }
      }
    }
    getProducts()
  }, [])

  // Fetch recommendations from Azure Functions
  useEffect(() => {
    const getRecommendations = async () => {
      try {
        const customerId = api.getCustomerInfo()?.id || 'anonymous'
        const res = await api.getRecommendations({
          customer_id: customerId,
          limit: 4
        })
        
        if (res.data?.status === 'success' && res.data?.recommendations) {
          setRecommendations(res.data.recommendations)
          
          // Track recommendations viewed
          if (window.faroTracker) {
            window.faroTracker.trackEvent('recommendations_viewed', {
              count: res.data.recommendations.length,
              cloud_provider: res.data.cloud_provider,
              source: 'homepage',
              customer_id: customerId
            })
          }
        }
      } catch (error) {
        console.error('Failed to load recommendations:', error)
        if (window.faroTracker) {
          window.faroTracker.trackError(error, {
            context: 'recommendations_load_homepage'
          })
        }
      } finally {
        setRecommendationsLoading(false)
      }
    }
    getRecommendations()
  }, [])

  const onCartItemAdd = async (e) => {
    e.stopPropagation()
    const productId = e.currentTarget.getAttribute('data-product-id')
    const product = products.find(p => p.id === productId);
    const startTime = Date.now();
    
    setPending(pending => ({
      ...pending,
      [productId]: true
    }))
    
    // Start user action for add-to-cart (wrapped in try-catch to prevent blocking)
    if (window.faroTracker && product) {
      try {
        // Check if startUserAction exists before calling
        if (typeof window.faroTracker.startUserAction === 'function') {
          console.log('🎯 Calling startUserAction for add-to-cart');
          const userAction = window.faroTracker.startUserAction('add-to-cart', {
            product_id: productId,
            product_name: product.name,
            product_price: product.price,
            product_category: product.category || 'uncategorized',
            product_type: product.type || 'unknown',
            page: 'homepage'
          }, {
            triggerName: 'button_click',
            importance: 'normal'
          });
          console.log('✅ startUserAction returned:', userAction);
        } else {
          console.warn('⚠️ window.faroTracker.startUserAction is not a function');
        }
        
        if (window.faroTracker.trackEvent) {
          window.faroTracker.trackEvent('product_interaction', {
            product_id: productId,
            product_name: product.name,
            product_price: product.price,
            product_category: product.category || 'uncategorized',
            product_type: product.type || 'unknown',
            action: 'add_to_cart_attempt',
            page: 'homepage'
          });
        }
        
        if (window.faroTracker.trackConversionStep) {
          window.faroTracker.trackConversionStep('product_view', {
            product_id: productId,
            product_category: product.category,
            source: 'homepage'
          });
        }
      } catch (faroError) {
        // Don't let Faro errors block the cart operation
        // Silently fail - tracking is optional
      }
    }
    
    try {
      const response = await api.cartItemAdd(productId, 1)
      console.log(response)
      await app.refreshCart(true) // Track cart refresh
      
      // Track successful cart addition
      if (window.faroTracker && product) {
        window.faroTracker.trackEvent('product_added_to_cart', {
          product_id: productId,
          product_name: product.name,
          product_price: parseFloat(product.price),
          product_category: product.category || 'uncategorized',
          quantity: 1,
          duration: Date.now() - startTime,
          source_page: 'homepage',
          success: true
        });
        
        // Use app context cart tracking
        app.trackCartOperation?.('add', productId, 1);
      }
    } catch (error) {
      console.error('Add to cart failed:', error);
      if (window.faroTracker) {
        window.faroTracker.trackError(error, {
          context: 'add_to_cart_homepage',
          product_id: productId,
          duration: Date.now() - startTime
        });
      }
    } finally {
      delete pending[productId]
      setPending(pending => ({
        ...pending,
        [productId]: undefined
      }))
    }
  }

  const onCartItemRemove = async (e) => {
    e.stopPropagation()
    const productId = e.currentTarget.getAttribute('data-product-id')
    const product = products.find(p => p.id === productId);
    const startTime = Date.now();
    
    setPending(pending => ({
      ...pending,
      [productId]: true
    }))
    
    // Track product removal attempt
    if (window.faroTracker && product) {
      window.faroTracker.trackEvent('product_interaction', {
        product_id: productId,
        product_name: product.name,
        product_price: product.price,
        product_category: product.category || 'uncategorized',
        action: 'remove_from_cart_attempt',
        page: 'homepage'
      });
    }
    
    try {
      const response = await api.cartItemRemove(productId, 1)
      console.log(response)
      await app.refreshCart(true) // Track cart refresh
      
      // Track successful cart removal
      if (window.faroTracker && product) {
        window.faroTracker.trackEvent('product_removed_from_cart', {
          product_id: productId,
          product_name: product.name,
          product_price: parseFloat(product.price),
          product_category: product.category || 'uncategorized',
          quantity: 1,
          duration: Date.now() - startTime,
          source_page: 'homepage',
          success: true
        });
        
        // Use app context cart tracking
        app.trackCartOperation?.('remove', productId, 1);
      }
    } catch (error) {
      console.error('Remove from cart failed:', error);
      if (window.faroTracker) {
        window.faroTracker.trackError(error, {
          context: 'remove_from_cart_homepage',
          product_id: productId,
          duration: Date.now() - startTime
        });
      }
    } finally {
      delete pending[productId]
      setPending(pending => ({
        ...pending,
        [productId]: undefined
      }))
    }
  }

  const productCards = []
  if (!products.length) {
    for (var i = 0; i < 3; i++) {
      productCards.push(
        <Grid.Column key={i}>
          <Card fluid>
            <Placeholder>
              <Placeholder.Image className='card-image' rectangular />
            </Placeholder>
            <Card.Content>
              <Card.Header>
                <Placeholder>
                  <Placeholder.Header>
                    <Placeholder.Line />
                  </Placeholder.Header>
                </Placeholder>
              </Card.Header>
              <Card.Description>
              <Placeholder>
                <Placeholder.Paragraph>
                  <Placeholder.Line />
                  <Placeholder.Line />
                </Placeholder.Paragraph>
              </Placeholder>
              </Card.Description>
            </Card.Content>
            <Card.Content extra>
              <Grid container verticalAlign='middle'>
                <Grid.Column style={{ paddingLeft: 0, paddingRight: 0 }} width={9}>
                  <Placeholder>
                    <Placeholder.Header>
                      <Placeholder.Line />
                    </Placeholder.Header>
                  </Placeholder>
                </Grid.Column>
                <Grid.Column textAlign='right' width={7}>
                  <Placeholder>
                    <Placeholder.Header>
                      <Placeholder.Line />
                    </Placeholder.Header>
                  </Placeholder>
                </Grid.Column>
              </Grid>
            </Card.Content>
          </Card>
        </Grid.Column>
      )
    }
  } else {
    for (var i in products) {
      let product = products[i]
      productCards.push(
        <Grid.Column key={product.id}>
          <Card fluid>
            <div className='card-image' style={{
              backgroundImage: `url('/api/v1/content/${product.filename}')`
            }}></div>
            <Card.Content>
              <Card.Header>{product.name}</Card.Header>
              <Card.Description>
                {product.description}
              </Card.Description>
            </Card.Content>
            <Card.Content extra>
              <Grid container verticalAlign='middle'>
                <Grid.Column style={{ paddingLeft: 0, paddingRight: 0 }} width={9}>
                  { app.cartHas(product.id) ?
                    <Button animated='vertical' color='standard' compact data-product-id={product.id} disabled={pending[product.id]} fluid onClick={onCartItemRemove} size='large'>
                      <Button.Content visible>
                        <Icon name='check' /> Added
                      </Button.Content>
                      <Button.Content hidden>
                        <Icon name='x' /> Remove
                      </Button.Content>
                    </Button>
                    :
                    <Button color='teal' compact data-product-id={product.id} disabled={pending[product.id]} fluid onClick={onCartItemAdd} size='large'>
                      <Icon name='cart plus' /> Add to cart
                    </Button>
                  }
                </Grid.Column>
                <Grid.Column textAlign='right' width={7}>
                  <Header as='h3'>${product.price}</Header>
                </Grid.Column>
              </Grid>
            </Card.Content>
          </Card>
        </Grid.Column>
      )
    }
  }

  // Build recommendation cards
  const recommendationCards = []
  if (recommendationsLoading) {
    for (var i = 0; i < 4; i++) {
      recommendationCards.push(
        <Grid.Column key={i}>
          <Card fluid>
            <Placeholder>
              <Placeholder.Image className='card-image' rectangular />
            </Placeholder>
            <Card.Content>
              <Card.Header>
                <Placeholder>
                  <Placeholder.Header>
                    <Placeholder.Line />
                  </Placeholder.Header>
                </Placeholder>
              </Card.Header>
            </Card.Content>
          </Card>
        </Grid.Column>
      )
    }
  } else if (recommendations.length > 0) {
    for (var i in recommendations) {
      let rec = recommendations[i]
      recommendationCards.push(
        <Grid.Column key={rec.id}>
          <Card fluid>
            <div className='card-image' style={{
              backgroundImage: `url('/api/v1/content/${rec.filename}')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center'
            }}></div>
            <Card.Content>
              <Card.Header>{rec.name}</Card.Header>
              <Card.Description>
                <Icon name='star' color='yellow' />
                {rec.rating} rating
              </Card.Description>
            </Card.Content>
            <Card.Content extra>
              <Grid container verticalAlign='middle'>
                <Grid.Column style={{ paddingLeft: 0, paddingRight: 0 }} width={9}>
                  { app.cartHas(rec.id) ?
                    <Button animated='vertical' color='standard' compact data-product-id={rec.id} disabled={pending[rec.id]} fluid onClick={onCartItemAdd} size='large'>
                      <Button.Content visible>
                        <Icon name='check' /> Added
                      </Button.Content>
                      <Button.Content hidden>
                        <Icon name='x' /> Remove
                      </Button.Content>
                    </Button>
                    :
                    <Button color='teal' compact data-product-id={rec.id} disabled={pending[rec.id]} fluid onClick={onCartItemAdd} size='large'>
                      <Icon name='cart plus' /> Add to cart
                    </Button>
                  }
                </Grid.Column>
                <Grid.Column textAlign='right' width={7}>
                  <Header as='h3'>${rec.price}</Header>
                </Grid.Column>
              </Grid>
            </Card.Content>
          </Card>
        </Grid.Column>
      )
    }
  }

  return (
    <div>
      <Segment textAlign='center' style={{ margin: 0, padding: '4em 0' }} vertical>
        <SectionHeader />
        <SectionTitle title={title} subtitle={subtitle} />
        <Grid container columns={3} doubling stackable stretched>
          {productCards}
        </Grid>
      </Segment>
      
      {/* Recommendations Section - Powered by Azure */}
      {(recommendationsLoading || recommendations.length > 0) && (
        <Segment textAlign='center' style={{ margin: 0, padding: '4em 0', backgroundColor: '#f9fafb' }} vertical>
          <Container>
            <Header as='h2' style={{ marginBottom: '0.5em' }}>
              <Icon name='magic' color='blue' />
              Recommended for You
            </Header>
            <p style={{ color: '#666', marginBottom: '2em' }}>
              <Icon name='cloud' color='blue' />
              Powered by Multi-Cloud AI (Azure Functions)
            </p>
            <Grid container columns={4} doubling stackable stretched>
              {recommendationCards}
            </Grid>
          </Container>
        </Segment>
      )}
      
      <SectionFooter/>
    </div>
  )
}

export default HomePage
